import { useState, useEffect } from 'react';
import axios from 'axios';
import { readStorage, writeStorage } from '../safeStorage';

export default function Login({ onSuccess }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [dark, setDark]         = useState(() => readStorage('sono-dark-mode') === 'true');

  const styles = makeStyles(dark);

  useEffect(() => {
    document.body.style.background = dark ? '#121212' : '#f5f5f5';
    writeStorage('sono-dark-mode', dark);
  }, [dark]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/login', { username, password }, { withCredentials: true });
      onSuccess(res.data); // { username } — token stays in HttpOnly cookie, never in JS
    } catch (err) {
      setError(err.response?.data?.message ?? 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.themeRow}>
          <button style={styles.themeBtn} type="button" onClick={() => setDark(d => !d)}>
            {dark ? '☀️ Light' : '🌙 Dark'}
          </button>
        </div>
        <h2 style={styles.title}>SonoClient</h2>
        <p style={styles.subtitle}>Sign in to continue</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Username</label>
          <input
            style={styles.input}
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="username"
            required
          />

          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          {error && <p style={styles.error}>{error}</p>}

          <button style={styles.btn} type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

const makeStyles = (dark) => ({
  page: {
    minHeight: '100vh', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: dark ? '#121212' : '#f5f5f5', fontFamily: 'sans-serif',
  },
  card: {
    background: dark ? '#1e1e1e' : '#fff', color: dark ? '#e0e0e0' : '#222',
    borderRadius: 10,
    padding: '2.5rem 2rem', width: '100%', maxWidth: 360,
    boxShadow: dark ? '0 2px 16px rgba(0,0,0,0.6)' : '0 2px 16px rgba(0,0,0,0.10)',
  },
  themeRow: { display: 'flex', justifyContent: 'flex-end', margin: '-1.5rem -1rem 0 0' },
  themeBtn: {
    padding: '4px 10px', background: 'transparent', color: 'inherit',
    border: `1px solid ${dark ? '#4a4a4a' : '#ccc'}`, borderRadius: 4,
    cursor: 'pointer', fontSize: 12,
  },
  title:    { margin: '0 0 4px', fontSize: 22, fontWeight: 700, textAlign: 'center' },
  subtitle: { margin: '0 0 24px', color: dark ? '#9e9e9e' : '#888', fontSize: 13, textAlign: 'center' },
  form:     { display: 'flex', flexDirection: 'column', gap: 10 },
  label:    { fontSize: 13, fontWeight: 600, color: dark ? '#bdbdbd' : '#444' },
  input: {
    padding: '9px 12px', borderRadius: 5,
    border: `1px solid ${dark ? '#4a4a4a' : '#ccc'}`, fontSize: 14, outline: 'none',
    background: dark ? '#2a2a2a' : '#fff', color: dark ? '#e0e0e0' : '#222',
  },
  error: { margin: 0, color: '#c62828', fontSize: 13 },
  btn: {
    marginTop: 8, padding: '10px 0', background: '#1976d2',
    color: '#fff', border: 'none', borderRadius: 5,
    fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
});
